"use client";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTheme } from "next-themes";
import { useERP } from "@/lib/erp/store";
import { useI18n } from "@/lib/erp/i18n";
import { ERP_MODULES_V2, MODULE_GROUPS, type ERPModuleV2, type ERPSubModule, ALL_PAGES } from "@/lib/erp/modulesV2";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  Search, Bell, Sun, Moon, Menu, ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight,
  LogOut, RotateCcw, Settings as SettingsIcon, LayoutDashboard, Globe,
} from "lucide-react";

export interface WorkspaceProps {
  activePageId: string;
  onNavigate: (pageId: string) => void;
  onLogout: () => void;
  userEmail: string;
  children: React.ReactNode;
}

export function Workspace({ activePageId, onNavigate, onLogout, userEmail, children }: WorkspaceProps) {
  const erp = useERP();
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  // M15 fix: use reactive hook so language toggle updates UI
  const { lang, toggle: toggleLang, t } = useI18n();
  const [collapsed, setCollapsed] = React.useState(false);
  const [expandedMods, setExpandedMods] = React.useState<Set<string>>(new Set([ERP_MODULES_V2[0].id]));
  const [expandedSubs, setExpandedSubs] = React.useState<Set<string>>(new Set());
  const [query, setQuery] = React.useState("");
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  const toggleMod = (id: string) => setExpandedMods(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSub = (id: string) => setExpandedSubs(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Active context
  const activeCtx = ALL_PAGES[activePageId];
  const activeModule = activeCtx ? ERP_MODULES_V2.find(m => m.id === activeCtx.moduleId) : undefined;
  const activeGroup = activeModule ? MODULE_GROUPS.find(g => g.code === activeModule.group) : undefined;
  const activeSub = activeCtx ? activeModule?.subModules.find(s => s.id === activeCtx.subModuleId) : undefined;

  // Search across all pages
  const searchResults = React.useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const out: { moduleId: string; moduleName: string; subId: string; subName: string; pageId: string; pageTitle: string; group: string }[] = [];
    for (const mod of ERP_MODULES_V2) {
      for (const sm of mod.subModules) {
        for (const p of sm.pages) {
          if (p.label.toLowerCase().includes(q) || mod.nameEn.toLowerCase().includes(q) || sm.label.toLowerCase().includes(q)) {
            out.push({ moduleId: mod.id, moduleName: mod.nameEn, subId: sm.id, subName: sm.label, pageId: p.id, pageTitle: p.label, group: mod.group });
          }
        }
      }
    }
    return out.slice(0, 12);
  }, [query]);

  const handleNavigate = (pageId: string) => {
    onNavigate(pageId);
    setMobileOpen(false);
    setQuery("");
  };

  const handleReset = () => {
    if (confirm("Reset all data back to seed values? This cannot be undone.")) {
      erp.resetStore();
      toast({ title: "Data reset", description: "All entities restored to seed state." });
    }
  };

  // Pending approvals count for the bell
  const pendingCount = erp.approvals.filter(a => a.status === "Pending").length;
  const lowStockCount = erp.products.filter(p => erp.getStock(p.id) < p.reorderPoint).length;

  return (
    <div className="bg-background flex h-screen flex-col overflow-hidden">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background px-3 sm:px-4">
        {/* Mobile hamburger */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden"><Menu className="size-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 overflow-hidden">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <div className="flex h-full flex-col overflow-hidden">
              <div className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
                <div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 font-bold text-slate-900 text-sm shadow-sm">Ω</div>
                <span className="font-semibold">OMEGA ERP</span>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                <SidebarContent collapsed={false} activePageId={activePageId} activeModule={activeModule} expandedMods={expandedMods} expandedSubs={expandedSubs} toggleMod={toggleMod} toggleSub={toggleSub} onNavigate={handleNavigate} />
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Collapse toggle (desktop only) */}
        <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={() => setCollapsed(c => !c)}>
          {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
        </Button>

        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="flex size-7 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 font-bold text-slate-900 text-sm shadow-sm">Ω</div>
          <span className="hidden font-semibold sm:inline">OMEGA Distribution ERP</span>
        </div>

        {/* Search */}
        <div className="relative ml-2 hidden max-w-md flex-1 sm:block">
          <Search className="text-muted-foreground absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search pages, modules..." className="pl-8" />
          {searchResults.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-md border bg-popover p-1 shadow-lg">
              {searchResults.map(r => (
                <button key={r.pageId} onClick={() => handleNavigate(r.pageId)} className="hover:bg-accent flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left text-sm">
                  <span className="font-medium">{r.pageTitle}</span>
                  <span className="text-muted-foreground text-xs">{r.moduleName} · {r.subName}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ml-auto flex items-center gap-1">
          {/* Language toggle */}
          <Button variant="ghost" size="sm" className="text-xs gap-1.5" onClick={toggleLang} title="Switch language">
            <Globe className="size-4" />
            <span className="hidden sm:inline">{lang === "en" ? "বাংলা" : "English"}</span>
          </Button>

          {/* Theme toggle */}
          {mounted && (
            <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} title="Toggle theme">
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
          )}

          {/* Notifications */}
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="size-4" />
                {pendingCount > 0 && <span className="bg-destructive absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full text-[9px] text-white">{pendingCount}</span>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel>Notifications</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {pendingCount > 0 && (
                <DropdownMenuItem onClick={() => { handleNavigate("wf-pending"); setNotifOpen(false); }}>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{pendingCount} pending approval(s)</span>
                    <span className="text-muted-foreground text-xs">Awaiting your decision</span>
                  </div>
                </DropdownMenuItem>
              )}
              {lowStockCount > 0 && (
                <DropdownMenuItem onClick={() => { handleNavigate("inv-reorder-list"); setNotifOpen(false); }}>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{lowStockCount} product(s) below reorder point</span>
                    <span className="text-muted-foreground text-xs">Replenishment required</span>
                  </div>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { handleNavigate("track-map"); setNotifOpen(false); }}>3 shipments in transit</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2">
                <Avatar className="size-7"><AvatarFallback className="bg-primary text-primary-foreground text-xs">AR</AvatarFallback></Avatar>
                <span className="hidden text-sm font-medium sm:inline">{userEmail.split("@")[0]}</span>
                <ChevronDown className="text-muted-foreground size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{userEmail.split("@")[0]}</span>
                  <span className="text-muted-foreground text-xs">{userEmail}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleNavigate("md-org-setup")}><SettingsIcon className="size-4" /> Settings</DropdownMenuItem>
              <DropdownMenuItem onClick={handleReset}><RotateCcw className="size-4" /> Reset Data</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="text-destructive"><LogOut className="size-4" /> Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Breadcrumb bar */}
      {(activeGroup || activeModule) && (
        <div className="bg-muted/30 flex items-center gap-1.5 border-b px-4 py-1.5 text-xs">
          {activeGroup && <span className="font-medium" style={{ color: activeGroup.accent }}>{activeGroup.nameEn}</span>}
          {activeModule && <><ChevronRight className="text-muted-foreground size-3" /><span className="text-muted-foreground">{activeModule.nameEn}</span></>}
          {activeSub && <><ChevronRight className="text-muted-foreground size-3" /><span className="text-muted-foreground">{activeSub.label}</span></>}
          {activeCtx && <><ChevronRight className="text-muted-foreground size-3" /><span className="text-foreground font-medium">{activeCtx.page.label}</span></>}
        </div>
      )}

      {/* Body: sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        <aside className={cn("hidden border-r bg-card lg:flex lg:flex-col overflow-hidden", collapsed ? "w-16" : "w-64")}>
          <SidebarContent collapsed={collapsed} activePageId={activePageId} activeModule={activeModule} expandedMods={expandedMods} expandedSubs={expandedSubs} toggleMod={toggleMod} toggleSub={toggleSub} onNavigate={handleNavigate} />
        </aside>

        <main className="flex-1 overflow-y-auto omega-sidebar-scroll">
          <div className="mx-auto max-w-[1600px] p-4 sm:p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ collapsed, activePageId, activeModule, expandedMods, expandedSubs, toggleMod, toggleSub, onNavigate }: {
  collapsed: boolean;
  activePageId: string;
  activeModule?: ERPModuleV2;
  expandedMods: Set<string>;
  expandedSubs: Set<string>;
  toggleMod: (id: string) => void;
  toggleSub: (id: string) => void;
  onNavigate: (pageId: string) => void;
}) {
  // M15 fix: subscribe to language so sidebar updates reactively
  const { t, lang } = useI18n();
  if (collapsed) {
    return (
      <nav className="omega-sidebar-scroll flex-1 min-h-0 overflow-y-auto p-2 flex flex-col gap-1">
        <button onClick={() => onNavigate("home")} className="hover:bg-accent flex size-10 items-center justify-center rounded-md" title={t("Executive Tower")}>
          <LayoutDashboard className="size-5" />
        </button>
        {ERP_MODULES_V2.map(mod => {
          const Icon = mod.icon;
          return (
            <button
              key={mod.id}
              onClick={() => { toggleMod(mod.id); onNavigate(mod.subModules[0].pages[0].id); }}
              className={cn("hover:bg-accent flex size-10 items-center justify-center rounded-md", activeModule?.id === mod.id && "bg-accent")}
              title={lang === "bn" ? mod.nameBn : mod.nameEn}
            >
              <Icon className="size-5" />
            </button>
          );
        })}
      </nav>
    );
  }
  return (
    <nav className="omega-sidebar-scroll flex-1 min-h-0 overflow-y-auto p-2 flex flex-col gap-1">
      <button onClick={() => onNavigate("home")} className={cn("flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors", activePageId === "home" ? "bg-gradient-to-r from-amber-500/15 to-orange-500/10 text-amber-700 dark:text-amber-300" : "hover:bg-accent")}>
        <LayoutDashboard className="size-4" /> {t("Executive Control Tower")}
        {activePageId === "home" && <Badge variant="outline" className="ml-auto text-[9px] px-1 py-0 h-4 bg-emerald-500/10 text-emerald-600 border-emerald-500/30">LIVE</Badge>}
      </button>
      {MODULE_GROUPS.map(group => {
        const mods = ERP_MODULES_V2.filter(m => m.group === group.code);
        if (mods.length === 0) return null;
        return (
          <div key={group.code} className="flex flex-col gap-0.5">
            <div className="text-muted-foreground mt-3 px-3 text-[10px] font-semibold uppercase tracking-wider" style={{ color: group.accent }}>{lang === "bn" ? group.nameBn : group.nameEn}</div>
            {mods.map(mod => <ModuleItem key={mod.id} mod={mod} activePageId={activePageId} activeModule={activeModule} expandedMods={expandedMods} expandedSubs={expandedSubs} toggleMod={toggleMod} toggleSub={toggleSub} onNavigate={onNavigate} />)}
          </div>
        );
      })}
    </nav>
  );
}

function ModuleItem({ mod, activePageId, activeModule, expandedMods, expandedSubs, toggleMod, toggleSub, onNavigate }: {
  mod: ERPModuleV2;
  activePageId: string;
  activeModule?: ERPModuleV2;
  expandedMods: Set<string>;
  expandedSubs: Set<string>;
  toggleMod: (id: string) => void;
  toggleSub: (id: string) => void;
  onNavigate: (pageId: string) => void;
}) {
  const Icon = mod.icon;
  const { lang } = useI18n();
  const isExpanded = expandedMods.has(mod.id);
  const isActiveMod = activeModule?.id === mod.id;
  const group = MODULE_GROUPS.find(g => g.code === mod.group);
  const accent = group?.accent || "#475569";

  return (
    <div className="flex flex-col">
      <button
        onClick={() => toggleMod(mod.id)}
        className={cn("flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] font-medium transition-colors hover:bg-accent/50")}
        style={isActiveMod ? { color: accent } : undefined}
      >
        <Icon className="size-4 shrink-0" />
        <span className="flex-1 text-left truncate">{lang === "bn" ? mod.nameBn : mod.nameEn}</span>
        <span className="text-[9px] font-mono text-muted-foreground/60">{mod.code.replace("M-","")}</span>
        {isExpanded ? <ChevronDown className="size-3.5 text-muted-foreground" /> : <ChevronRight className="size-3.5 text-muted-foreground" />}
      </button>
      {isExpanded && (
        <div className="ml-3 flex flex-col gap-0.5 border-l pl-2">
          {mod.subModules.map(sm => <SubModuleItem key={sm.id} sm={sm} activePageId={activePageId} expandedSubs={expandedSubs} toggleSub={toggleSub} onNavigate={onNavigate} />)}
        </div>
      )}
    </div>
  );
}

function SubModuleItem({ sm, activePageId, expandedSubs, toggleSub, onNavigate }: {
  sm: ERPSubModule;
  activePageId: string;
  expandedSubs: Set<string>;
  toggleSub: (id: string) => void;
  onNavigate: (pageId: string) => void;
}) {
  const Icon = sm.icon;
  const isExpanded = expandedSubs.has(sm.id);
  const isActivePage = sm.pages.some(p => p.id === activePageId);
  const single = sm.pages.length === 1;
  return (
    <div className="flex flex-col">
      <button
        onClick={() => single ? onNavigate(sm.pages[0].id) : toggleSub(sm.id)}
        className={cn("flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] transition-colors hover:bg-accent/40", isActivePage && "font-medium")}
      >
        <Icon className="text-muted-foreground size-3.5 shrink-0" />
        <span className="flex-1 text-left truncate">{sm.label}</span>
        <span className="text-[9px] text-muted-foreground/60">{sm.pages.length}</span>
        {!single && (isExpanded ? <ChevronDown className="text-muted-foreground size-3" /> : <ChevronRight className="text-muted-foreground size-3" />)}
      </button>
      {!single && isExpanded && (
        <div className="ml-3 flex flex-col gap-0 border-l pl-1.5">
          {sm.pages.map(p => {
            const PIcon = p.icon;
            const isActive = p.id === activePageId;
            return (
              <button
                key={p.id}
                onClick={() => onNavigate(p.id)}
                className={cn("flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] transition-colors", isActive ? "font-medium" : "hover:bg-accent/40 text-foreground/70")}
                style={isActive ? { background: `${MODULE_GROUPS.find(g => g.code === ERP_MODULES_V2.find(m => m.subModules.find(s => s.id === sm.id))?.group)?.accent}1A`, color: MODULE_GROUPS.find(g => g.code === ERP_MODULES_V2.find(m => m.subModules.find(s => s.id === sm.id))?.group)?.accent, borderLeft: `2px solid ${MODULE_GROUPS.find(g => g.code === ERP_MODULES_V2.find(m => m.subModules.find(s => s.id === sm.id))?.group)?.accent}` } : undefined}
              >
                <PIcon className="size-3 shrink-0" />
                <span className="truncate">{p.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
